/**
 * P3-16 — NAAQS attainment on the environmental scorecard.
 *
 * An attainment designation is an EPA regulatory finding. The environmental
 * scorer used to return 'Attainment' (score 85) whenever the research text did
 * not contain "non-attainment", including when there was no research at all.
 * The absence of evidence therefore rendered as a known green designation
 * (document evidence `state: 'known'`, no sources) while the narrative
 * correctly said the status still had to be determined.
 *
 * Designation evidence here is the site-research `airQualityRegion` text that
 * reaches the scorer. Report generation withholds coordinate-based AI research
 * before scoring (`projectResearchForReport`), so only admissible research
 * text arrives.
 *
 * The EPA Green Book county designation persisted on the SED
 * (`environmental.data.naaqsDesignations`) outranks that text when it is
 * coverage-backed (`assessEpaDesignation`); the research-text reading below
 * applies, unchanged, only when EPA was not assessed. Its "not listed" result
 * means "no current nonattainment or maintenance listing", never "Attainment".
 */
import type { EpaAttainmentData } from '../services/dataProviders/epaAttainment';
import { describeEpaDesignation, NOT_IN_ATTAINMENT, readEpaDesignation } from './sed/epaDesignation';

export type AttainmentDesignationState = 'nonattainment' | 'attainment' | 'not-verified';

export interface AttainmentDesignationAssessment {
  state: AttainmentDesignationState;
  /**
   * Metric score. For 'not-verified' this is a display-only placeholder: the
   * scorer excludes the metric from the Air Quality weighted mean.
   */
  score: number;
  value: string;
  explanation: string;
}

/** Scores unchanged from the pre-P3-16 scorer for evidenced designations. */
export const ATTAINMENT_SCORE = 85;
export const NONATTAINMENT_SCORE = 25;
/** Display-only placeholder for an unverified designation (never weighted). */
export const ATTAINMENT_NOT_VERIFIED_PLACEHOLDER_SCORE = 50;

export const ATTAINMENT_NOT_VERIFIED_EXPLANATION =
  'No EPA attainment or nonattainment designation evidence is available for this location. NAAQS attainment status is not verified and is excluded from the Air Quality score.';

/** Superset of the previous `includes('non-attainment')` test. */
const NONATTAINMENT_RE = /non-?attainment/i;
const ATTAINMENT_RE = /\battainment\b/i;
/**
 * Wording that names attainment without establishing it ("attainment status
 * must be determined", "Unverified; consult the primary source.").
 */
const UNDETERMINED_RE = /\b(?:unknown|undetermined|unverified|unconfirmed|unclear|uncertain|pending|consult\w*|tbd|not\s+(?:yet\s+)?(?:been\s+)?(?:determined|verified|confirmed|established|available|known)|to\s+be\s+(?:determined|confirmed|verified)|(?:must|should|needs?\s+to)\s+be)\b/i;

const PART_COUNTY_CAVEAT = ' Part-county areas do not establish that the site lies inside the area; confirm against the area boundary in 40 CFR Part 81.';

/**
 * The coverage-backed EPA Green Book designation; null when EPA was not
 * assessed (no designation, or a result from before the national table).
 * Against research text it is never greener, with two deliberate
 * exceptions where the Green Book is authoritative over stale screening
 * text claiming nonattainment:
 *  - maintenance only: Attainment 85, "Attainment (maintenance area)"
 *    (redesignated to attainment, CAA 107(d)(3)(E); the 175A plan is stated,
 *    not scored), where the text alone would score 25;
 *  - not listed: Not verified (excluded, never Attainment), where the text
 *    alone would score 25 (e.g. a revoked standard).
 * Listed nonattainment is always 25. Text naming a standard the export does
 * not include (PM-2.5 (2024) in the 2026-08-31 export) or a designation dated
 * after it is outside what the listing can contradict; it does not change the
 * score here; the owner accepted the two exceptions on 30 September 2026, and the
 * `epa-attainment-fips` cross-check raises it to a warning instead (S4,
 * `epaExportGaps`).
 */
function assessEpaDesignation(epa: EpaAttainmentData | null | undefined): AttainmentDesignationAssessment | null {
  const reading = readEpaDesignation(epa);
  if (!epa || reading === 'not-assessed') return null;
  const listing = describeEpaDesignation(epa);
  const partCounty = (status: 'nonattainment' | 'maintenance'): string =>
    (epa.designations ?? []).some(d => d.status === status && d.extent === 'part') ? PART_COUNTY_CAVEAT : '';
  if (reading === 'nonattainment') {
    return {
      state: 'nonattainment',
      score: NONATTAINMENT_SCORE,
      value: `Non-attainment (${epa.classification ? `${epa.classification}, ` : ''}EPA Green Book)`,
      explanation: `${listing}.${partCounty('nonattainment')}`,
    };
  }
  if (reading === 'maintenance') {
    return {
      state: 'attainment',
      score: ATTAINMENT_SCORE,
      value: 'Attainment (maintenance area)',
      explanation: `${listing}. A maintenance area has been redesignated to attainment (CAA §107(d)(3)(E)) and operates under a §175A maintenance plan whose contingency measures can still bind new sources; confirm permitting requirements with the state air agency.${partCounty('maintenance')}`,
    };
  }
  return {
    state: 'not-verified',
    score: ATTAINMENT_NOT_VERIFIED_PLACEHOLDER_SCORE,
    value: 'No current nonattainment/maintenance listing',
    explanation: `${listing}. NAAQS attainment is not verified and is excluded from the Air Quality score.`,
  };
}

/**
 * @param researchText  admissible site-research `airQualityRegion` text
 * @param epa           EPA Green Book designation from the SED; when absent or
 *                      not coverage-backed the research-text reading applies
 */
export function assessAttainmentDesignation(researchText: unknown, epa?: EpaAttainmentData | null): AttainmentDesignationAssessment {
  const listed = assessEpaDesignation(epa);
  if (listed) return listed;
  const text = typeof researchText === 'string' ? researchText.trim() : '';
  // Owner D02 (30 September 2026): keep the conservative 25 for negated or
  // dated nonattainment text; failed attainment also cannot become green.
  // This does not adopt the broader current-designation text-reader policy.
  if (NONATTAINMENT_RE.test(text) || NOT_IN_ATTAINMENT.test(text)) {
    return { state: 'nonattainment', score: NONATTAINMENT_SCORE, value: 'Non-attainment', explanation: '' };
  }
  if (ATTAINMENT_RE.test(text) && !UNDETERMINED_RE.test(text)) {
    return { state: 'attainment', score: ATTAINMENT_SCORE, value: 'Attainment', explanation: '' };
  }
  return {
    state: 'not-verified',
    score: ATTAINMENT_NOT_VERIFIED_PLACEHOLDER_SCORE,
    value: 'Not verified',
    explanation: ATTAINMENT_NOT_VERIFIED_EXPLANATION,
  };
}
